import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { useTrialStatus, TRIAL_URGENT_THRESHOLD_DAYS } from '@hooks/useTrialStatus';

/**
 * Dashboard banner for the two moments that matter: the trial is about to end, and the
 * trial has ended.
 *
 * An organism rather than a molecule because it reads server state and owns a call to
 * action. Only the OWNER can actually pay, so everyone else gets an explanation instead of
 * a button that would fail — the same owner / non-owner split the suspended-organization
 * page already uses.
 */
export const TrialExpiredBanner: FC = () => {
  const history = useHistory();
  const role = useAppSelector((state: RootState) => state.user.role);
  const { isOnTrial, isBillingLocked, daysRemaining, isEndingSoon } =
    useTrialStatus();

  const isOwner = role === UserRoles.OWNER;

  if (isBillingLocked) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-700/40 dark:bg-amber-900/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <AlertTriangle
              className="h-6 w-6 flex-shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-200">
                Your free trial has ended
              </p>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                Everything is still here and your team can keep reading. Creating
                and editing are paused until a payment method is added.
                {!isOwner &&
                  ' Ask the account owner to add one to restore full access.'}
              </p>
            </div>
          </div>

          {isOwner && (
            <Button
              onClick={() => history.push('/trial-expired')}
              className="flex-shrink-0">
              Choose a plan
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Quiet countdown while the trial is still running, and only near the end — a banner
  // shown on day one of a long trial is just noise by the time it matters.
  if (isOnTrial && isEndingSoon && daysRemaining !== null) {
    const urgent = daysRemaining <= TRIAL_URGENT_THRESHOLD_DAYS;

    return (
      <div
        className={
          urgent
            ? 'rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700/40 dark:bg-amber-900/20'
            : 'rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/40 dark:bg-blue-900/20'
        }>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Clock
              className={
                urgent
                  ? 'h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400'
                  : 'h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400'
              }
              aria-hidden="true"
            />
            <p
              className={
                urgent
                  ? 'text-sm text-amber-900 dark:text-amber-200'
                  : 'text-sm text-blue-900 dark:text-blue-200'
              }>
              {daysRemaining <= 0
                ? 'Your free trial ends today.'
                : daysRemaining === 1
                  ? 'Your free trial ends tomorrow.'
                  : `Your free trial ends in ${daysRemaining} days.`}{' '}
              {isOwner
                ? 'Add a payment method to keep working without interruption.'
                : 'Ask the account owner to add a payment method.'}
            </p>
          </div>

          {/* /billing, not /owner-billing: OwnerBillingPage is registered under that path,
              and only for OWNER — the same condition gating this button.
              addPayment=1 opens the add-card modal on arrival, so the button delivers what
              it promises instead of dropping the user on a page to hunt for it. */}
          {isOwner && (
            <Button
              variant="secondary"
              onClick={() => history.push('/billing?addPayment=1')}
              className="flex-shrink-0">
              Add payment method
            </Button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default TrialExpiredBanner;
